import { type ReactNode } from 'react';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';

export interface NextStepBannerProps {
  /** Título do banner — verbo no imperativo (ex.: "Próximo passo: emitir DAMSP"). */
  title: string;
  /** Descrição curta do que precisa ser feito e por quê. */
  description: ReactNode;
  /** Texto do botão de ação primária. */
  actionLabel?: string;
  /** Callback ao clicar no botão. */
  onAction?: () => void;
  /** Variante visual. `success` pra passos opcionais; `info` (padrão) pra ações conduzindo o fluxo. */
  variant?: 'info' | 'success';
  /** Fixo no topo do contêiner (use com `position: relative` no pai). */
  sticky?: boolean;
  /** Ícone customizado (padrão: ArrowRight — ver nota abaixo sobre o ícone base). */
  icon?: ReactNode;
  className?: string;
}

/**
 * Banner padronizado pra conduzir o usuário ao PRÓXIMO PASSO natural depois
 * de uma ação que mudou o estado do registro (aprovação, fechamento,
 * substituição etc.). Cópia estrutural do protótipo (`NextStepBanner.tsx`).
 *
 * Consumido principalmente via `<TimelineNextStepBanner />`, que deriva
 * título/descrição/ação do passo `current` de uma `<ProgressTimeline />`.
 */
export function NextStepBanner({
  title,
  description,
  actionLabel,
  onAction,
  variant = 'info',
  sticky = false,
  icon,
  className,
}: NextStepBannerProps) {
  const Icone = variant === 'success' ? CheckCircle2 : Sparkles;
  const tom =
    variant === 'success'
      ? 'border-success/40 bg-success/5 text-foreground **:data-icon:text-success'
      : 'border-primary/30 bg-primary/5 text-foreground **:data-icon:text-primary';

  return (
    <aside
      role="region"
      aria-label="Próximo passo"
      className={cn('rounded-xl border p-4 sm:p-5', sticky && 'sticky top-2 z-10 shadow-xs backdrop-blur-sm', tom, className)}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="shrink-0 rounded-full bg-background/60 p-2" data-icon>
            {icon ?? <Icone className="h-4 w-4" aria-hidden />}
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold leading-tight">{title}</p>
            <div className="text-xs leading-relaxed text-muted-foreground">{description}</div>
          </div>
        </div>
        {actionLabel && onAction && (
          <Button size="sm" onClick={onAction} className="shrink-0 self-start sm:self-auto">
            {actionLabel}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden />
          </Button>
        )}
      </div>
    </aside>
  );
}

export default NextStepBanner;
